import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
    Button, Dropdown, Form, Space, Table, Tag, Tooltip, Typography, theme
} from "antd";
import {
    CheckCircleOutlined,
    CheckCircleTwoTone,
    DeleteOutlined,
    DownOutlined,
    EditOutlined,
    EuroCircleOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FilePdfTwoTone,
    FilterOutlined,
    MoreOutlined,
    PauseCircleTwoTone,
    PlusOutlined,
    StopOutlined,
    SyncOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { InscriptionLight, StatutInscription } from "../../../services/inscription";
import { APPLICATION_DATE_FORMAT, APPLICATION_DATE_TIME_FORMAT } from "../../../utils/FormUtils";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import { InscriptionViewProps } from "./types";
import { AdminSearchFilter } from "../../../components/common/AdminSearchFilter";
import { AdminFilterPanel } from "../../../components/common/AdminFilterPanel";
import { SelectionActionBar } from "../../../components/common/SelectionActionBar";
import { ReinscriptionBadge } from "../../../components/common/ReinscriptionBadge";
import { buildUrlWithParams, DOCUMENT_CONTENT_ENDPOINT } from "../../../services/services";
import {
    getLibelleNiveauScolaire,
    getStatutInscriptionOptions,
} from "../../../components/common/commoninputs";
import { getInscriptionFilterConfig } from "./inscriptionFilters";
import { SituationPaiement, StatutReglement } from "../../../services/paiement";
import { PaiementBadge, getLibelleStatutReglement } from "../../../components/paiements/PaiementBadge";
import { ModalPaiements } from "../../../components/paiements/ModalPaiements";
import { getStatutReglement } from "../../../components/paiements/paiementUtils";

const { Text } = Typography;

// ─── Constantes de design ─────────────────────────────────────────────────────

/**
 * Mapping statut -> configuration visuelle (couleur badge Ant, label, icône).
 * Centralisé ici pour éviter la répétition et faciliter l'extension.
 */
const STATUT_CONFIG: Record<
    StatutInscription,
    { color: string; label: string; icon: JSX.Element; tagColor: string }
> = {
    [StatutInscription.VALIDEE]: {
        color: "success",
        label: "Validée",
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
        tagColor: "success",
    },
    [StatutInscription.PROVISOIRE]: {
        color: "warning",
        label: "Provisoire",
        icon: <PauseCircleTwoTone twoToneColor="#faad14" />,
        tagColor: "warning",
    },
    [StatutInscription.LISTE_ATTENTE]: {
        color: "error",
        label: "Liste d'attente",
        icon: <WarningOutlined style={{ color: "#ff4d4f" }} />,
        tagColor: "error",
    },
    [StatutInscription.REFUSE]: {
        color: "default",
        label: "Refusée",
        icon: <StopOutlined style={{ color: "#8c8c8c" }} />,
        tagColor: "default",
    },
};

// ─── Sous-composant : Badge de statut ────────────────────────────────────────

/**
 * Affiche le statut sous forme de Tag coloré avec icône.
 * Remplace les tooltips isolés par un élément lisible directement dans la cellule,
 * ce qui améliore la scannabilité de la table sans nécessiter d'interaction.
 */
const StatutBadge: FunctionComponent<{ statut: StatutInscription }> = ({ statut }) => {
    const config = STATUT_CONFIG[statut] ?? STATUT_CONFIG[StatutInscription.REFUSE];
    return (
        <Tag
            icon={config.icon}
            color={config.tagColor}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: 6 }}
        >
            {config.label}
        </Tag>
    );
};

// ─── Sous-composant : Boutons d'action d'une ligne ───────────────────────────

interface RowActionsProps {
    inscription: InscriptionLight;
    application: string;
    onValidate: (id: number) => void;
    onDelete: (id: number) => void;
    onPaiements: (idInscription: number) => void;
}

/**
 * Actions d'une ligne : les trois gestes du quotidien restent des boutons, le reste part dans un
 * menu. Six boutons alignés imposaient une colonne de 215 px qu'il fallait figer à droite ; trois
 * boutons et un menu tiennent en 145 px, ce qui supprime le besoin de figement.
 *
 * Modifier / Valider / Paiements sont visibles car ce sont les gestes de la période de
 * finalisation des inscriptions.
 */
const RowActions: FunctionComponent<RowActionsProps> = ({
    inscription,
    application,
    onValidate,
    onDelete,
    onPaiements,
}) => {
    const navigate = useNavigate();
    const basePath = application === "COURS_ENFANT" ? "/coursEnfants" : "/coursAdultes";
    const dejaValidee = inscription.statut === StatutInscription.VALIDEE;

    const ouvrirDocument = () => {
        const url = buildUrlWithParams(DOCUMENT_CONTENT_ENDPOINT, { idDocument: inscription.idDocument });
        window.open(`${process.env.REACT_APP_BASE_URL_API_V1}${url}`, "_blank");
    };

    const libellePdf = inscription.documentPending
        ? "PDF en cours de génération"
        : inscription.idDocument
            ? "Télécharger le PDF"
            : "PDF non disponible";

    const menuItems: MenuProps["items"] = [
        {
            key: "consulter",
            icon: <EyeOutlined />,
            label: "Consulter",
            onClick: () => navigate(`${basePath}/${inscription.idInscription}?readonly=true`),
        },
        {
            key: "pdf",
            icon: inscription.documentPending ? <SyncOutlined spin /> : <FilePdfTwoTone twoToneColor="#1890ff" />,
            label: libellePdf,
            disabled: inscription.documentPending || !inscription.idDocument,
            onClick: ouvrirDocument,
        },
        { type: "divider" },
        {
            key: "supprimer",
            icon: <DeleteOutlined />,
            label: "Supprimer",
            danger: true,
            onClick: () => onDelete(inscription.idInscription),
        },
    ];

    return (
        <Space size={4} wrap={false}>
            <Tooltip title="Modifier">
                <Button
                    size="small"
                    icon={<EditOutlined />}
                    style={{ color: "#1890ff", borderColor: "#1890ff" }}
                    onClick={() => navigate(`${basePath}/${inscription.idInscription}?readonly=false`)}
                    {...({ testid: `modifier-inscription-${inscription.idInscription}` } as any)}
                />
            </Tooltip>

            <Tooltip title={dejaValidee ? "Déjà validée" : "Valider"}>
                <Button
                    size="small"
                    icon={<CheckCircleOutlined />}
                    style={{
                        color: dejaValidee ? "#d9d9d9" : "#52c41a",
                        borderColor: dejaValidee ? "#d9d9d9" : "#52c41a",
                    }}
                    disabled={dejaValidee}
                    onClick={() => onValidate(inscription.idInscription)}
                    {...({ testid: `valider-inscription-${inscription.idInscription}` } as any)}
                />
            </Tooltip>

            <Tooltip title="Paiements">
                <Button
                    size="small"
                    icon={<EuroCircleOutlined />}
                    style={{ color: "#fa8c16", borderColor: "#fa8c16" }}
                    onClick={() => onPaiements(inscription.idInscription)}
                    {...({ testid: `paiements-inscription-${inscription.idInscription}` } as any)}
                />
            </Tooltip>

            <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                <Button
                    size="small"
                    icon={<MoreOutlined />}
                    {...({ testid: `autres-actions-inscription-${inscription.idInscription}` } as any)}
                />
            </Dropdown>
        </Space>
    );
};

// ─── Sous-composant : En-tête des résultats ──────────────────────────────────

interface ResultsHeaderProps {
    totalCount: number;
    uniqueCount: number;
    onExport: () => void;
}

/**
 * Bande d'en-tête au-dessus de la table.
 * Regroupe les méta-informations (compteurs) et l'action d'export dans une zone
 * dédiée, séparée visuellement des actions de ligne. Cela respecte le principe
 * de proximité : ce qui agit sur l'ensemble est séparé de ce qui agit sur une ligne.
 */
const ResultsHeader: FunctionComponent<ResultsHeaderProps> = ({ totalCount, uniqueCount, onExport }) => (
    <div
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 16px",
            background: "#fafafa",
            borderRadius: "8px 8px 0 0",
            borderBottom: "1px solid #f0f0f0",
            flexWrap: "wrap",
            gap: 8,
        }}
    >
        {/* Compteurs */}
        <Space size={8}>
            <Text type="secondary" style={{ fontSize: 13 }}>
                <strong style={{ color: "rgba(0,0,0,0.85)" }}>{totalCount}</strong> élève(s) ·{" "}
                <strong style={{ color: "rgba(0,0,0,0.85)" }}>{uniqueCount}</strong> inscription(s)
            </Text>
        </Space>

        {/* Export */}
        <Tooltip title="Exporter tous les résultats dans un fichier Excel">
            <Button
                icon={<FileExcelOutlined />}
                onClick={onExport}
                style={{ color: "#52c41a", borderColor: "#52c41a" }}
            >
                Exporter ({totalCount})
            </Button>
        </Tooltip>
    </div>
);

// ─── Sous-composant : État vide ───────────────────────────────────────────────

/**
 * Placeholder affiché quand aucun résultat ne correspond aux filtres.
 * Remplace le div texte brut par un état vide structuré et invitant à agir.
 */
const EmptyResults: FunctionComponent = () => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "48px 24px",
            background: "#fff",
            borderRadius: 12,
            border: "1px dashed #d9d9d9",
            marginTop: 16,
            gap: 8,
        }}
        role="status"
        aria-live="polite"
    >
        <FilterOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
        <Text style={{ color: "#8c8c8c", fontSize: 15 }}>Aucune inscription ne correspond aux filtres</Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
            Modifiez les critères de recherche ci-dessus pour affiner les résultats.
        </Text>
    </div>
);

// ─── Composant principal ──────────────────────────────────────────────────────

export const InscriptionDesktopView: FunctionComponent<InscriptionViewProps> = ({
    application,
    setDataSource,
    type,
    dataSource,
    selectedInscriptions,
    setSelectedInscriptions,
    periodesOptions,
    onValidateInscription,
    onValidateInscriptions,
    onDeleteInscription,
    onDeleteInscriptions,
    onSearch,
    onExport,
}) => {
    const [form] = Form.useForm();
    const { token } = theme.useToken();

    useEffect(() => {
        if (periodesOptions && periodesOptions.length > 0) {
            form.setFieldValue("idPeriode", periodesOptions[0].value);
        }
    }, [periodesOptions]);

    // ─── Logique de recherche ────────────────────────────────────────────────

    const buildSearchCriteria = () => {
        const { nom, prenom, telephone, statut, noInscription, idPeriode, reinscription } = form.getFieldsValue();
        let dateInscription = form.getFieldValue("dateInscription");
        if (dateInscription) {
            dateInscription = dayjs(dateInscription).format(APPLICATION_DATE_FORMAT);
        }
        const niveaux = form.getFieldValue("niveau");
        const niveauxInternes = form.getFieldValue("niveauInterne");
        const searchType = application === "COURS_ENFANT" ? "ENFANT" : "ADULTE";
        return {
            nom: nom ?? null,
            prenom: prenom ?? null,
            telephone: telephone ?? null,
            statut: statut ?? null,
            dateInscription: dateInscription ?? null,
            niveaux: niveaux ?? null,
            niveauxInternes: niveauxInternes ?? null,
            noInscription: noInscription ?? null,
            idPeriode: idPeriode ?? null,
            reinscription: reinscription ?? null,
            type: searchType,
        };
    };

    const doSearch = async () => {
        await onSearch(buildSearchCriteria());
    };

    // ─── Filtres statiques + optionnels (ajoutés à la demande) ────────────────

    const { staticFilters, optionalFilters } = getInscriptionFilterConfig(application, periodesOptions);
    const [activeOptionalNames, setActiveOptionalNames] = useState<string[]>([]);

    const activeOptionalFilters = optionalFilters.filter((f) => activeOptionalNames.includes(f.name));
    const inactiveOptionalFilters = optionalFilters.filter((f) => !activeOptionalNames.includes(f.name));

    const addFilter = (name: string) => setActiveOptionalNames((prev) => [...prev, name]);

    /**
     * Vide un critère et relance la recherche. On retire aussi le champ de la grille s'il est
     * optionnel, sans quoi un filtre masqué continuerait de s'appliquer.
     */
    const clearFilter = (name: string) => {
        form.setFieldValue(name, undefined);
        setActiveOptionalNames((prev) => prev.filter((n) => n !== name));
        doSearch();
    };
    const removeFilter = clearFilter;

    // ─── Métriques de la sélection ────────────────────────────────────────────

    const uniqueInscriptionCount = useMemo(
        () => new Set(dataSource?.map((i) => i.idInscription)).size,
        [dataSource]
    );

    // ─── Paiements ────────────────────────────────────────────────────────────

    const [paiementsOpen, setPaiementsOpen] = useState<boolean>(false);
    const [idInscriptionPaiements, setIdInscriptionPaiements] = useState<number | undefined>(undefined);

    const ouvrirPaiements = (idInscription: number) => {
        setIdInscriptionPaiements(idInscription);
        setPaiementsOpen(true);
    };

    /**
     * Met à jour les lignes de l'inscription concernée sans relancer la recherche : recharger la
     * liste ici perdrait les critères courants, `loadInscriptions` étant appelé sans paramètres.
     */
    const rafraichirLignes = (situation: SituationPaiement) => {
        setDataSource((lignes) => lignes.map((ligne) => ligne.idInscription === situation.idCible
            ? { ...ligne, montantTotal: situation.montantTotal, montantRegle: situation.montantRegle }
            : ligne));
    };

    // ─── Définition des colonnes ──────────────────────────────────────────────

    /**
     * Colonnes dimensionnées pour tenir sans défilement horizontal sur un écran 1366, condition
     * pour que la colonne Actions n'ait pas besoin d'être figée — un figement fait glisser les
     * autres colonnes dessous, ce qui nuit plus qu'il n'aide.
     *
     * Deux regroupements y contribuent : le téléphone rejoint la cellule Élève (déjà porteuse de la
     * ville), et les deux niveaux partagent une colonne.
     */
    const columns: ColumnsType<InscriptionLight> = [
        {
            title: "N° inscription",
            dataIndex: "noInscription",
            key: "noInscription",
            width: 120,
            render: (value: string) => (
                <Text code style={{ fontSize: 13 }}>
                    {value}
                </Text>
            ),
        },
        {
            title: "Élève",
            key: "eleve",
            width: 240,
            render: (_, record) => (
                <div>
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, lineHeight: 1.4 }}>
                        <Text strong>{record.nom} {record.prenom}</Text>
                        {record.reinscription && <ReinscriptionBadge />}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
                        {[record.ville, record.mobile].filter(Boolean).join(" · ") || "—"}
                    </Text>
                </div>
            ),
        },
        {
            /**
             * Les deux niveaux partagent une colonne : ils se lisent ensemble et occupaient 200 px
             * à eux deux. Le niveau publique n'existe que pour les cours enfants.
             */
            title: "Niveau",
            key: "niveaux",
            width: 130,
            render: (_, record) => (
                <Space size={4} wrap>
                    {application === "COURS_ENFANT" && record.niveau && (
                        <Tag style={{ borderRadius: 4, fontSize: 12, marginInlineEnd: 0 }}>
                            {getLibelleNiveauScolaire(record.niveau as any)}
                        </Tag>
                    )}
                    {record.niveauInterne ? (
                        <Tag color="blue" style={{ borderRadius: 4, fontSize: 12, marginInlineEnd: 0 }}>
                            {record.niveauInterne}
                        </Tag>
                    ) : (
                        <Text type="secondary">—</Text>
                    )}
                </Space>
            ),
        },
        {
            title: "Statut",
            dataIndex: "statut",
            key: "statut",
            width: 140,
            filters: getStatutInscriptionOptions().map((o) => ({
                text: o.label as string,
                value: o.value as string,
            })),
            onFilter: (value, record) => record.statut === value,
            render: (value: StatutInscription) => <StatutBadge statut={value} />,
        },
        {
            title: "Date inscription",
            dataIndex: "dateInscription",
            key: "dateInscription",
            width: 130,
            sorter: (a, b) =>
                dayjs(a.dateInscription, APPLICATION_DATE_TIME_FORMAT).unix() -
                dayjs(b.dateInscription, APPLICATION_DATE_TIME_FORMAT).unix(),
            render: (value: string) => (
                <Text style={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}>
                    {dayjs(value, APPLICATION_DATE_TIME_FORMAT).format(APPLICATION_DATE_FORMAT)}
                </Text>
            ),
        },
        {
            /**
             * Le paiement porte sur l'inscription, pas sur l'élève : les lignes d'une même fratrie
             * affichent donc toutes le même badge. C'est voulu.
             */
            title: "Paiement",
            key: "paiement",
            width: 130,
            filters: Object.values(StatutReglement).map((statut) => ({
                text: getLibelleStatutReglement(statut),
                value: statut as string,
            })),
            // Filtrage côté client : la liste est intégralement chargée et paginée en mémoire
            onFilter: (value, record) =>
                getStatutReglement(record.montantTotal, record.montantRegle) === value,
            render: (_, record: InscriptionLight) => (
                <PaiementBadge montantTotal={record.montantTotal} montantRegle={record.montantRegle} />
            ),
        },
        {
            // Volontairement non figée : un figement ferait glisser les autres colonnes dessous.
            title: "Actions",
            key: "actions",
            width: 145,
            render: (_, inscription: InscriptionLight) => (
                <RowActions
                    inscription={inscription}
                    application={application}
                    onValidate={onValidateInscription}
                    onDelete={onDeleteInscription}
                    onPaiements={ouvrirPaiements}
                />
            ),
        },
    ];

    // ─── Sélection multiple (inchangée) ──────────────────────────────────────

    const rowSelection = {
        selectedRowKeys: selectedInscriptions.map((i) => i.id),
        onChange: (_keys: React.Key[], rows: InscriptionLight[]) => {
            setSelectedInscriptions(rows);
        },
    };

    // ─── Rendu ───────────────────────────────────────────────────────────────

    const hasResults = dataSource && dataSource.length > 0;

    return (
        <div>
            {/* ── Bandeau filtres, pleine largeur ── */}
            <Form
                name="adminInscriptionDesktop"
                layout="vertical"
                autoComplete="off"
                form={form}
            >
                <AdminFilterPanel
                    form={form}
                    filterDefs={[...staticFilters, ...optionalFilters]}
                    // La période est toujours renseignée : l'afficher comme filtre actif serait du bruit.
                    ignoredNames={["idPeriode"]}
                    onClearFilter={clearFilter}
                >
                    <AdminSearchFilter
                        layout="grid"
                        doSearch={doSearch}
                        inputFilters={[...staticFilters, ...activeOptionalFilters]}
                        removableFilterNames={activeOptionalNames}
                        onRemoveFilter={removeFilter}
                        extraActions={
                            <Dropdown
                                trigger={["click"]}
                                disabled={inactiveOptionalFilters.length === 0}
                                menu={{
                                    items: inactiveOptionalFilters.map((f) => ({ key: f.name, label: f.libelle })),
                                    onClick: ({ key }) => addFilter(key),
                                }}
                            >
                                <Button icon={<PlusOutlined />} {...({ testid: "ajouter-filtre" } as any)}>
                                    Ajouter un filtre <DownOutlined />
                                </Button>
                            </Dropdown>
                        }
                    />
                </AdminFilterPanel>
            </Form>

            <div style={{ minWidth: 0 }}>
                {/* ── Zone résultats ── */}
                {hasResults ? (
                    <div
                        style={{
                            background: token.colorBgContainer,
                            borderRadius: token.borderRadiusLG,
                            border: `1px solid ${token.colorBorder}`,
                            overflow: "hidden",
                            boxShadow: token.boxShadowSecondary,
                        }}
                    >
                        {/* En-tête résultats avec compteurs + export */}
                        <ResultsHeader
                            totalCount={dataSource.length}
                            uniqueCount={uniqueInscriptionCount}
                            onExport={onExport}
                        />

                        {/* Barre d'actions groupées — apparaît uniquement si sélection active */}
                        {selectedInscriptions.length > 0 && (
                            <div style={{ padding: "8px 16px 0 16px" }}>
                                <SelectionActionBar
                                    selectedCount={selectedInscriptions.length}
                                    itemLabel="inscription"
                                    onValidate={() => onValidateInscriptions(selectedInscriptions)}
                                    onDelete={() => onDeleteInscriptions(selectedInscriptions)}
                                    onCancel={() => setSelectedInscriptions([])}
                                />
                            </div>
                        )}

                        {/* Table */}
                        <div style={{ padding: "0 0 8px 0" }}>
                            <Table
                                rowSelection={{ type: "checkbox", ...rowSelection }}
                                columns={columns}
                                dataSource={dataSource}
                                rowKey={(record) => record.id}
                                /**
                                 * Largeur minimale plutôt que `max-content` : le tableau remplit
                                 * le conteneur quand il est plus large, et ne défile que sous
                                 * 1100 px — cas devenu rare depuis le passage des filtres en
                                 * bandeau.
                                 */
                                scroll={{ x: 1100 }}
                                size="middle"
                                pagination={{
                                    showSizeChanger: true,
                                    pageSizeOptions: ["10", "20", "50", "100"],
                                    defaultPageSize: 10,
                                    showTotal: (total, range) => (
                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                            {range[0]}–{range[1]} sur{" "}
                                            <strong>{total}</strong> résultat(s)
                                        </Text>
                                    ),
                                }}
                                /**
                                 * Mise en évidence des lignes selon le statut :
                                 * un fond très légèrement teinté renforce la lecture
                                 * sans surcharger visuellement la table.
                                 */
                                rowClassName={(record) => {
                                    if (record.statut === StatutInscription.VALIDEE) return "row-validated";
                                    if (record.statut === StatutInscription.LISTE_ATTENTE) return "row-waiting";
                                    return "";
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <EmptyResults />
                )}
            </div>

            <ModalPaiements
                open={paiementsOpen}
                setOpen={setPaiementsOpen}
                idInscription={idInscriptionPaiements}
                onChange={rafraichirLignes}
            />
        </div>
    );
};
